import { useAtom } from 'jotai'
import Cookies from 'js-cookie'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'

import { usePasskey } from '@/features/auth/hooks/use-passkey'
import { useResetClientStorage } from '@/features/auth/hooks/use-reset-client-storage'
import { useFullPath } from '@/hooks/use-full-path'
import { reset as resetAnalytics } from '@/lib/analytics'
import { withRefreshTokenLock } from '@/lib/auth/refresh-token-lock'
import {
  COOKIE_KEYS,
  ERROR_MESSAGES,
  LOGOUT_INTENT_EXPLICIT,
  LOGOUT_INTENT_FORCED_RESET,
} from '@/lib/configs/auth-config'
import { clearDeviceId, getOrCreateDeviceId } from '@/lib/utils/device-id'
import { extractEmailVerifiedFromAccessToken } from '@/lib/utils/token-util'
import { loginWithEmail as loginWithEmailAPI } from '@/service/auth.api'
import { verify2FALogin } from '@/service/user-profile.api'
import { authAtom, clearAuthState } from '@/state/auth'
import { emitAuthAnalytics } from '@/state/auth-analytics'
import { AuthState } from '@/types/auth'
import { ConfirmPasskeyLoginResponse } from '@/types/generated/common/auth'

export async function persistRefreshToken(refreshToken?: string | null) {
  if (!refreshToken) return

  const response = await fetch('/api/auth/session', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })

  if (!response.ok) {
    throw { status: response.status, message: 'Failed to persist auth session' }
  }
}

async function refreshAuthSessionFromCookie(): Promise<AuthState | null> {
  const response = await withRefreshTokenLock(() =>
    fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
  )

  if (!response.ok) return null

  const data = (await response.json()) as { success?: boolean; authState?: AuthState }
  return data.success && data.authState?.accessToken ? data.authState : null
}

export function useAuth() {
  const t = useTranslations('auth')
  const router = useRouter()
  const currentFullPath = useFullPath()
  const { loginWithPasskey } = usePasskey()
  const [authState, setAuthState] = useAtom(authAtom)
  const resetClientStorage = useResetClientStorage()

  const handlePasskeyResponse = useCallback(
    async (response: ConfirmPasskeyLoginResponse) => {
      if (response.success && response.user) {
        await persistRefreshToken(response.refreshToken)
        setAuthState({
          ...authState,
          isAuthenticated: true,
          user: response.user!,
          accessToken: response.accessToken || null,
          refreshToken: response.refreshToken || null,
          isLoading: false,
        })
        emitAuthAnalytics({ method: 'passkey', userId: response.user.userId, kind: 'login' })
      } else {
        throw { status: 400, message: response.errorMessage || t('passkeyLoginFailed') }
      }
    },
    [authState, setAuthState, t]
  )

  const loginWithEmail = useCallback(
    async ({
      email,
      password,
      skipAnalytics = false,
    }: {
      email: string
      password: string
      /** Signup logs in right after creating the account and emits `Sign up` itself. */
      skipAnalytics?: boolean
    }) => {
      if (authState.isLoading) throw { status: 400, message: t('waitPreviousLogin') }
      setAuthState({ ...authState, isLoading: true })

      try {
        const response = await loginWithEmailAPI({ email, password })

        if (response.errorMessage === ERROR_MESSAGES.PASSKEY_AUTH_USER) {
          const passkeyResponse = await loginWithPasskey({ email })
          await handlePasskeyResponse(passkeyResponse)
          return { requires2fa: false, requiresEmailVerification: false }
        }
        if (response.requires2fa) {
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            requires2fa: true,
            tempToken: response.tempToken || null,
            requiresEmailVerification: false,
          })
          return { requires2fa: true, requiresEmailVerification: false }
        } else if (response.success && response.user) {
          await persistRefreshToken(response.refreshToken)
          const requiresEmailVerification = !extractEmailVerifiedFromAccessToken(
            response.accessToken ?? ''
          )
          setAuthState({
            isAuthenticated: true,
            user: response.user!,
            accessToken: response.accessToken ?? null,
            refreshToken: response.refreshToken ?? null,
            isLoading: false,
            requires2fa: false,
            tempToken: null,
            requiresEmailVerification: requiresEmailVerification,
            isNewUser: false, // Normal email login is not "new user"
          })
          if (!skipAnalytics && !requiresEmailVerification) {
            emitAuthAnalytics({ method: 'email', userId: response.user.userId, kind: 'login' })
          }
          return {
            requires2fa: false,
            requiresEmailVerification: requiresEmailVerification,
            isNewUser: false,
          }
        } else {
          throw { status: 400, message: response.errorMessage || t('loginFailed') }
        }
      } catch (error) {
        setAuthState({ ...authState, isLoading: false })
        throw error
      }
    },
    [authState, handlePasskeyResponse, loginWithPasskey, setAuthState, t]
  )

  const verify2FA = useCallback(
    async ({
      tempToken,
      code,
      isBackupCode = false,
    }: {
      tempToken: string
      code: string
      isBackupCode?: boolean
    }) => {
      if (!authState.tempToken) throw { status: 400, message: t('noTempToken') }

      setAuthState({ ...authState, isLoading: true })

      try {
        // The request is a oneof: send either the authenticator TOTP code or a backup code.
        const response = await verify2FALogin({
          tempToken: tempToken,
          ...(isBackupCode ? { backupCode: code } : { totpCode: code }),
        })

        if (response.success) {
          await persistRefreshToken(response.refreshToken)
          setAuthState({
            ...authState,
            isAuthenticated: true,
            accessToken: response.accessToken ?? null,
            refreshToken: response.refreshToken ?? null,
            user: response.user || null,
            tempToken: null,
            pendingLoginMethod: undefined,
            isLoading: false,
          })
          // 2FA completes an email or Google login (passkey logins never reach this step).
          if (response.user?.userId) {
            emitAuthAnalytics({
              method: authState.pendingLoginMethod ?? 'email',
              userId: response.user.userId,
              kind: 'login',
            })
          }
        } else {
          throw { status: 400, message: response.errorMessage || t('verificationFailed') }
        }
      } catch (error) {
        setAuthState({ ...authState, isLoading: false })
        throw error
      }
    },
    [authState, setAuthState, t]
  )

  const googleLogin = useCallback(
    (redirect = currentFullPath) => {
      resetClientStorage()
      // Seed the device-id cookie before leaving the page: the OAuth callback
      // route reads it server-side to fill GoogleLoginRequest.device_id.
      getOrCreateDeviceId()
      const redirectUrl = encodeURIComponent(redirect)
      window.location.href = `/api/auth/google?redirect=${redirectUrl}`
    },
    [currentFullPath, resetClientStorage]
  )

  // Mark the just-verified email and bring the session token in line with it.
  // Authed endpoints gate on the access token's email_verified claim, not the
  // cookie flag, and the token we hold still predates verification — so refresh
  // it (and the persisted server-side session) to a verified one before we move
  // on. Best-effort: if the refresh fails we still clear the flag (so the layout
  // doesn't bounce back to /email-verify) and the dashboard's fail-closed account
  // check routes the stale-token user to onboarding until the next refresh.
  const updateEmailVerification = useCallback(
    async (isNewUser = false) => {
      const user = authState.user
      if (!user) return false

      let { accessToken, refreshToken } = authState
      try {
        const refreshed = await refreshAuthSessionFromCookie()
        if (refreshed?.accessToken) {
          accessToken = refreshed.accessToken
          refreshToken = refreshed.refreshToken
        } else {
          return false
        }
      } catch (error) {
        console.warn('Token refresh after email verification failed:', error)
        return false
      }

      setAuthState({
        ...authState,
        user: { ...user, emailVerified: true },
        accessToken,
        refreshToken,
        requiresEmailVerification: false,
        isNewUser,
      })
      return true
    },
    [authState, setAuthState]
  )

  // logout() used to clear authAtom (and localStorage) synchronously, on the same
  // tick as kicking off `router.replace('/login')` — but that's a SOFT transition,
  // so the /trade tree stays mounted until it commits. Clearing auth state right
  // then made the still-mounted dashboard re-render as logged-out mid-navigation
  // (the "guest mode" flash), and if that /login transition was ever slow, dropped,
  // or superseded by another effect's own navigation call, the user was stranded on
  // /trade already logged-out client-side. Fixed by splitting the two concerns:
  // logout() only fires navigation + the actual server-side session revocation;
  // the client-side state clear is deferred to the effect below, which only runs
  // once we've actually arrived at /login — so /trade never renders a broken
  // half-logged-out state, and a stalled navigation just leaves the user normally
  // logged in on /trade instead of stuck in limbo.
  const pathname = usePathname()
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  const logout = useCallback(() => {
    // The short-lived intent cookie lets the deferred cleanup effect below
    // distinguish an explicit logout from a just-logged-in user still
    // rendering /login, so cleanup only wipes state on a real logout.
    Cookies.set(COOKIE_KEYS.AUTH_LOGOUT_INTENT, LOGOUT_INTENT_EXPLICIT, {
      expires: 1 / (24 * 60),
      sameSite: 'lax',
      path: '/',
    })
    router.replace('/login')

    // router.replace is a soft transition that isn't guaranteed to land (it can
    // be pre-empted by another component's navigation call, or simply stall on a
    // slow/dropped RSC fetch). Force a hard navigation if the soft one hasn't
    // committed shortly, so logout always actually reaches /login.
    setTimeout(() => {
      if (pathnameRef.current !== '/login' && typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }, 800)

    if (typeof window !== 'undefined') {
      // device_id shares the refresh token's lifetime (backend contract) —
      // clear it alongside the server-side session revocation below.
      clearDeviceId()
      // Fire-and-forget: revokes the httpOnly refresh-token cookie server-side.
      // Not awaited — navigation shouldn't wait on it, and `keepalive` lets it
      // finish even if this tab/page is already gone by the time it resolves.
      void fetch('/api/auth/session', {
        method: 'DELETE',
        credentials: 'include',
        keepalive: true,
      })
    }
  }, [router])

  // Deferred client-state cleanup — see the comment above logout(). Runs once
  // we've actually arrived at /login with an explicit logout intent while the
  // client still thinks it's authenticated. `isAuthenticated` alone is NOT a
  // logout signal here: a successful login also renders /login authenticated
  // (the form writes auth state, then navigateAfterAuth awaits a network call
  // before leaving), and firing on that state wiped the freshly written
  // AUTH_STATE cookie — /api/auth/refresh then rebuilt the session from the
  // wiped blob, which is how users ended up logged in with `user: null`. The
  // logout-intent cookie (set by logout() and requestLoginRedirect()) is what
  // distinguishes the two.
  useEffect(() => {
    if (pathname !== '/login') return
    const logoutIntent = Cookies.get(COOKIE_KEYS.AUTH_LOGOUT_INTENT)
    const hasLogoutIntent =
      logoutIntent === LOGOUT_INTENT_EXPLICIT || logoutIntent === LOGOUT_INTENT_FORCED_RESET
    if (hasLogoutIntent) Cookies.remove(COOKIE_KEYS.AUTH_LOGOUT_INTENT, { path: '/' })
    if (!hasLogoutIntent) return
    // Reset before the residue check: forced logouts clear auth state first, so
    // the early return below would otherwise skip it. Forced ones already reset
    // in requestLoginRedirect(); reset() is not idempotent, so don't repeat it.
    if (logoutIntent === LOGOUT_INTENT_EXPLICIT) void resetAnalytics()
    const hasAuthResidue = Boolean(
      authState.isAuthenticated || authState.user || authState.accessToken || authState.refreshToken
    )
    if (!hasAuthResidue) return
    // Dashboard storage keys are namespaced by user id (see lib/safe-storage.ts);
    // capture it before clearing so cleanup targets the outgoing user's data.
    resetClientStorage(authState.user?.userId)
    // Routes through the atom's own setter (not a raw Cookies.remove) so the
    // in-memory authAtom flips to logged-out too — bypassing the setter here
    // left `isLoggedIn` reporting the outgoing user as still authenticated for
    // the rest of the tab's life (atomWithCookie only re-reads the cookie when
    // it's present; a removed cookie falls back to the last in-memory value),
    // which in turn meant ActiveExchangeSync's own `if (!isLoggedIn)
    // setSavedExchange(null)` safety net never fired.
    setAuthState(clearAuthState())
  }, [pathname, authState, resetClientStorage, setAuthState])

  // Initialize state by reading authentication information from cookies
  const initializeAuthFromCookies = useCallback(() => {
    if (typeof window === 'undefined') return

    try {
      const authStateCookie = Cookies.get(COOKIE_KEYS.AUTH_STATE)
      const authState = authStateCookie
        ? (JSON.parse(decodeURIComponent(authStateCookie)) as AuthState)
        : null
      if (authState) {
        setAuthState({
          isAuthenticated: Boolean(authState.accessToken),
          isLoading: false, // Always reset loading state on initialization
          user: authState.user,
          accessToken: authState.accessToken,
          refreshToken: authState.refreshToken,
          requires2fa: authState.requires2fa,
          tempToken: authState.tempToken,
          pendingLoginMethod: authState.pendingLoginMethod,
          requiresEmailVerification: authState.requiresEmailVerification,
        })
      }
    } catch (error) {
      console.error('Failed to initialize auth from cookies:', error)
    }
  }, [setAuthState])

  // Initialize authentication state from cookies on component mount

  useEffect(() => {
    const timer = setTimeout(() => {
      initializeAuthFromCookies()
    }, 0)

    return () => clearTimeout(timer)
  }, [initializeAuthFromCookies])

  return {
    // Auth state
    ...authState,
    isGoogleLogin: Boolean(authState.user?.googleId),
    hasPassword: Boolean(authState.user?.hasPassword),

    // Actions
    loginWithEmail,
    verify2FA,
    googleLogin,
    logout,
    initializeAuthFromCookies,
    handlePasskeyResponse,
    updateEmailVerification,
  }
}
